/**
 * Typed API calls for `docs/API.md` §2-§5: communes, canvassing sessions,
 * the door assignment state machine, and the building view.
 *
 * Every function here is a thin {@link apiFetch} wrapper — no caching, no
 * business logic — per `docs/CONVENTIONS.md` §4 ("All API calls go through
 * `src/api/client.ts`"). Payload shapes come from `api/types.ts`, itself
 * derived from `api/schema.d.ts`; nothing here re-describes a response by
 * hand.
 */
import { apiFetch } from './client';

import type {
  Assignment,
  Building,
  Commune,
  LeaveResult,
  Paginated,
  PositionInputRequest,
  Session,
  SessionCreateRequest,
  SessionStatePoll,
  SessionWithExtras,
} from './types';

/** A WGS 84 point, as every geometry in the contract (`docs/API.md` §1). */
export type GeoPoint = Session['start_point'];

/**
 * `GET /communes/`: the caller's communes, with non-nominative coverage
 * counts (`docs/API.md` §2).
 *
 * @returns One page of the caller's communes.
 */
export function getCommunes(): Promise<Paginated<Commune>> {
  return apiFetch<Paginated<Commune>>('communes/');
}

/** The `state` filter `GET /sessions/` accepts. */
export type SessionStateFilter = 'open' | 'closed' | 'all';

export interface ListSessionsOptions {
  /** Defaults to `"open"` server-side when omitted. */
  state?: SessionStateFilter;
  /** Adds `distance_m` to each row, computed from this point. */
  near?: GeoPoint;
}

/**
 * `GET /sessions/`: the caller's communes' sessions, oldest open first.
 *
 * @param options - Optional `state` filter and `near` point.
 * @returns One page of sessions.
 */
export function listSessions(
  options: ListSessionsOptions = {},
): Promise<Paginated<SessionWithExtras>> {
  const search = new URLSearchParams();
  if (options.state) {
    search.set('state', options.state);
  }
  if (options.near) {
    search.set('near', `${options.near.longitude},${options.near.latitude}`);
  }
  const query = search.toString();
  return apiFetch<Paginated<SessionWithExtras>>(`sessions/${query ? `?${query}` : ''}`);
}

/**
 * `POST /sessions/`: open a new session; the caller becomes its first
 * participant.
 *
 * @param body - Commune, name, kind, visibility and start point.
 * @returns The newly opened session.
 */
export function createSession(body: SessionCreateRequest): Promise<SessionWithExtras> {
  return apiFetch<SessionWithExtras>('sessions/', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * `GET /sessions/{id}/`: session detail.
 *
 * @param id - The session id.
 * @returns The session.
 */
export function getSession(id: number): Promise<SessionWithExtras> {
  return apiFetch<SessionWithExtras>(`sessions/${id}/`);
}

/**
 * `POST /sessions/{id}/join/`.
 *
 * @param id - The session id.
 * @returns The session, with the caller now among `participants`.
 * @throws {ApiError} `409 session_closed` for a closed session.
 */
export function joinSessionRequest(id: number): Promise<SessionWithExtras> {
  return apiFetch<SessionWithExtras>(`sessions/${id}/join/`, { method: 'POST' });
}

/**
 * `POST /sessions/{id}/leave/`: release the caller's pending door, if any.
 *
 * @param id - The session id.
 * @returns How many doors were released (0 or 1).
 */
export function leaveSessionRequest(id: number): Promise<LeaveResult> {
  return apiFetch<LeaveResult>(`sessions/${id}/leave/`, { method: 'POST' });
}

/**
 * `POST /sessions/{id}/position/`: report (or clear) the caller's live
 * position.
 *
 * @param id - The session id.
 * @param point - The caller's current position, or `null` to clear it
 *   (geolocation refused or lost).
 */
export async function updatePosition(id: number, point: GeoPoint | null): Promise<void> {
  const body: PositionInputRequest = { point };
  await apiFetch<undefined>(`sessions/${id}/position/`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * `POST /sessions/{id}/close/`: release every pending door and freeze the
 * session's `closing_summary`.
 *
 * @param id - The session id.
 * @returns The now-closed session, with `closing_summary` filled in.
 * @throws {ApiError} `403 not_allowed` unless the caller created the
 *   session or is a commune admin.
 */
export function closeSessionRequest(id: number): Promise<SessionWithExtras> {
  return apiFetch<SessionWithExtras>(`sessions/${id}/close/`, { method: 'POST' });
}

/**
 * `GET /sessions/{id}/state/`: the light polling body — state, participants
 * and progress, cheap enough for a 15s poll.
 *
 * @param id - The session id.
 * @returns The polling body.
 */
export function getSessionState(id: number): Promise<SessionStatePoll> {
  return apiFetch<SessionStatePoll>(`sessions/${id}/state/`);
}

/**
 * `POST /sessions/{id}/next-door/`: hand out the next door — same building
 * first, then the closest candidate to `start_point`.
 *
 * @param id - The session id.
 * @returns The new (or still-pending) assignment.
 * @throws {ApiError} `409 no_door_left` once the candidate pool is
 *   exhausted, `409 session_closed` for a closed session.
 */
export function nextDoor(id: number): Promise<Assignment> {
  return apiFetch<Assignment>(`sessions/${id}/next-door/`, { method: 'POST' });
}

/**
 * `POST /sessions/{id}/skip-door/`: give up the door in hand and take a
 * different one. The door returns to the session's pool for the other pairs;
 * the caller is never offered it again for the rest of the session.
 *
 * @param id - The session id.
 * @returns The pending assignment on another door.
 * @throws {ApiError} `409 no_current_door` when nothing is in hand,
 *   `409 no_door_left` when the pool holds nothing else, `409 session_closed`
 *   for a closed session.
 */
export function skipDoor(id: number): Promise<Assignment> {
  return apiFetch<Assignment>(`sessions/${id}/skip-door/`, { method: 'POST' });
}

/**
 * `GET /sessions/{id}/current/`: the caller's pending assignment.
 *
 * @param id - The session id.
 * @returns The pending assignment, or `undefined` when the caller holds no
 *   door right now (`204`).
 */
export function getCurrentAssignment(id: number): Promise<Assignment | undefined> {
  return apiFetch<Assignment | undefined>(`sessions/${id}/current/`);
}

/**
 * `GET /sessions/{id}/building/`: the floors of the building holding the
 * caller's current door.
 *
 * @param id - The session id.
 * @returns The building view.
 * @throws {ApiError} `409 not_participating` with no current assignment.
 */
export function getBuilding(id: number): Promise<Building> {
  return apiFetch<Building>(`sessions/${id}/building/`);
}
